<style lang="scss" scoped>
.tangram-hotspot {
  @include flex-column-center;
}
</style>
<template>
  <div
    class="tangram-hotspot"
    :style="module.containerStyle"
    :data-tangram-module-id="module.id"
  >
    <img
      v-for="(hotArea, index) in data"
      :key="hotArea.data_id"
      ref="hotImage"
      v-tangram-expose="hotArea.tickerInfo"
      :src="hotArea.img | ossHttpVersion"
      :style="module.imgStyle"
      @click="onHotSpotClick(index, $event)"
    />
  </div>
</template>

<script>
import { cart } from '@/utils/cart/index'
import { API } from '@/api'
import { companyId } from '@/defines/user'
import { miniProgramEnv } from '@/defines/environment'
import { mpsCombination } from '@/utils/native_interactions/mps_combination'
import { autoRedirect } from '@/utils/auto_redirect'

const handleTypeEnum = {
  3: 'comboAddCart', // 套餐加购
  24: 'coupon', // 领取优惠券
  26: 'redirect', // 普通跳转
  98: 'skuAddCart' // sku商品加购
}

const hotClickHandler = {
  [handleTypeEnum[3]]: function (target) {
    this.addCart(target)
  },
  [handleTypeEnum[24]]: function (target) {
    const couponId = target
    this.receiveCoupon(couponId)
  },
  [handleTypeEnum[26]]: function (target, raw) {
    // redirectType为跳转类型
    // 1 || 6：其中1为跳转到cms二级页，6为跳转到普通H5页面
    // 50：点击提示【目前没有用到】
    // 2 || 3 || 4：跳转到原生，2为跳转到商品详情，3为商品分类，4为普通原生页面
    // 999：跳转小程序【目前无处理】
    const redirectType = Number(raw?.type)

    // 小程序跳直播
    if (redirectType === 4 && miniProgramEnv.isMiniProgram) {
      const path = target.slice(target.indexOf('#') + 1)
      const redirectToLive = /^plugin-private:\/\/[\s\S]+$/.test(path)
      if (redirectToLive) {
        return mpsCombination.navigateTo({ url: path })
      }
    } else {
      autoRedirect(target)
    }
  },
  [handleTypeEnum[98]]: function (target) {
    this.addCart(target)
  }
}

export default {
  props: {
    module: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      ssuDatas: [] // sku和套餐的商品数据
    }
  },
  computed: {
    data() {
      return this.module.data
    }
  },
  created() {
    // 1. 提取出包含商品和套餐的数据
    // 2. 通过加购SDK将热区商品进行绑定
    const ssuDatas = this.data.map(module => this.extractSsu(module))
    this.ssuDatas = [].concat(...ssuDatas)
    this.ssuDatas.forEach(ssu => {
      ssu.cartSsu = cart.getByUniqueId(ssu.uniqueId)
      ssu.count = ssu.cartSsu.count
      ssu.subscribeId = ssu.cartSsu.subscribe(good => {
        if (!ssu.addStatus) {
          ssu.cartSsu = good
          ssu.count = good.count
        }
      })
    })
  },
  mounted() {
    // 监听图片加载
    this.$refs.hotImage.forEach((hotImage, index) => {
      hotImage.onload = () => {
        this.data[index].hotImageLoaded = true
      }
    })
  },
  methods: {
    // 原始数据过滤出ssu和套餐加购数据
    extractSsu(module) {
      return module.hotData
        .filter(data => {
          return (
            data.smart_data.smart_type === 3 ||
            data.smart_data.smart_type === 98
          )
        })
        .map(data => {
          const uniqueId = data.smart_data.smart_target
          return {
            uniqueId,
            raw: data
          }
        })
    },
    // 点击热区
    onHotSpotClick(index, event) {
      const hotArea = this.data[index]
      if (!hotArea.hotImageLoaded) return

      const { pageX, pageY } = event
      const hotImageDomPosition = this.$refs.hotImage[
        index
      ].getBoundingClientRect()

      const scrollTop = this.getScrollTop(this.$refs.hotImage[index])
      const relativeX = pageX - hotImageDomPosition.left
      const relativeY = pageY - hotImageDomPosition.top - scrollTop

      const hotItem = this.data[index]
      const trackHotSpot = this.hotSpotTrack(hotItem.hotData, {
        relativeX,
        relativeY
      })
      if (!trackHotSpot) return

      // 埋点上报
      const module = this.data[index]
      this.tracerReport(module?.tickerInfo, 'click')

      this.handleHotSpot(trackHotSpot)
    },
    getScrollTop(ele) {
      if (ele.tagName === 'BODY') {
        return ele.scrollTop || document.documentElement.scrollTop
      } else {
        return ele.scrollTop ? ele.scrollTop : this.getScrollTop(ele.parentNode)
      }
    },
    // 热区定位
    hotSpotTrack(hotData, mousePosition) {
      const { relativeX, relativeY } = mousePosition
      return hotData.find(item => {
        const { startX, endX, startY, endY } = item.position
        return (
          startX <= relativeX &&
          endX >= relativeX &&
          startY <= relativeY &&
          endY >= relativeY
        )
      })
    },
    // 热区处理
    handleHotSpot(hotSpot) {
      const {
        // eslint-disable-next-line camelcase
        smart_data: { smart_type, smart_target }
      } = hotSpot

      const handleType = handleTypeEnum[smart_type]

      hotClickHandler[handleType].call(this, smart_target, hotSpot)
    },
    // 加购
    async addCart(uniqueId) {
      const ssu = this.ssuDatas.find(ssu => ssu.uniqueId === uniqueId)
      if (!ssu) return
      const originalCount = ssu.count

      try {
        ssu.addStatus = true
        ssu.count += 1

        // 前置判断当前是否满足加购条件
        const canBuy = this.preStatusHandler(ssu)
        if (!canBuy) throw new Error('当前商品不满足可加购条件')

        const res = await cart.debounceAdd(uniqueId)
        const newval = res.light_cart_ssus[0]
        if (newval?.status_remind_info?.goods_status === 9) {
          this.$toast(newval?.status_remind_info?.no_buy_time_remind)
        }
        const cartRes =
          res &&
          res.light_cart_ssus &&
          Array.isArray(res.light_cart_ssus) &&
          res.light_cart_ssus[0]
        const SRI = cartRes?.status_remind_info
        const addSuccess = this.postStatusHandler(SRI)
        if (!addSuccess) throw new Error('商品非可购买状态')

        this.$toast('加入购物车成功')
        ssu.addStatus = false

        if (miniProgramEnv.isMiniProgram) {
          mpsCombination.postMessage({
            data: {
              type: 'addCart'
            }
          })
        }
      } catch (e) {
        ssu.addStatus = false
        ssu.count = originalCount
      }
    },
    // 加购前置判断处理
    preStatusHandler(ssu) {
      const SRI = ssu?.cartSsu?.statusRemindInfo
      const PRI = ssu?.cartSsu?.promotionRemindInfo
      // 超出可购买数量
      if (SRI && SRI.available_amount && ssu.count > SRI.available_amount) {
        this.$toast(SRI.out_available_amount_remind || '超出可购买数量')
        return
      }
      // 优惠限购
      if (
        PRI &&
        PRI.over_promotion_remind &&
        ssu.count > PRI.promotion_goods_num
      ) {
        this.$toast(PRI.over_promotion_remind)
        return
      }
      return true
    },
    // 加购后置处理
    postStatusHandler(SRI) {
      const status = SRI?.goods_status
      let remindText = ''
      // 非可购买状态
      switch (status) {
        case 2:
          remindText = '商品仅在08:00-23:00售卖'
          break
        case 3:
          remindText = '商品已经卖光啦'
          break
        case 6:
          remindText = '不好意思，商品已经下线啦'
          break
        case 101:
          remindText = '商品不存在'
          break
        case 102:
          remindText = SRI.out_available_amount_remind
          break
      }
      if (remindText) {
        this.$toast(remindText)
        return
      }
      return true
    },
    // 领取优惠券
    async receiveCoupon(groupActionId) {
      try {
        await API.coupon.receiveCoupon({
          action_id: groupActionId,
          company_id: companyId
        })
        this.$toast({
          message: '领取成功',
          icon: 'success'
        })
      } catch (error) {
        if (error?.error?.msg) {
          this.$toast(error.error.msg)
        }
      }
    },
    // 埋点上报
    // tickerInfo: 埋点信息
    // type: 埋点类型[click等]
    tracerReport(tickerInfo, type) {
      this.$track(
        tickerInfo[type].spm,
        Object.assign({}, tickerInfo.data, tickerInfo[type].spmJson)
      )
    }
  }
}
</script>
